import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output,
    SimpleChanges
} from "@angular/core";
import { IStepperConfig, SelectComponent } from "@karma-games/angular-devkit/modules/controls-components";
import { BalancePipe } from "@karma-games/angular-devkit/src/lib/modules/pipes";
import { IAutoplayOptions } from "../core/round/store/interfaces";
import { KeyOf } from "@karma-games/angular-devkit/src/lib/std/utils";
import { Dictionary } from "lodash";
import IOption = SelectComponent.IOption;
import { IAutoplayOptionsConfig } from "../core/config/interfaces";

@Component({
    selector: "autoplay-options",
    templateUrl: "./autoplay-options.component.html",
    styleUrls: ["./autoplay-options.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutoplayOptionsComponent implements OnChanges {
    @Input() balanceAvailable: boolean = false;
    @Input() config: IAutoplayOptionsConfig & {
        autoCashOut: IStepperConfig & { enabled: boolean }
    } | undefined = undefined;
    @Input() currentBetAmount: number = 0;
    @Output() startAutoplay: EventEmitter<Partial<IAutoplayOptions>> = new EventEmitter();
    public roundCountOptions: IOption<number>[] = [];
    public stepperConfigs: Dictionary<IStepperConfig & { enabled?: boolean }> = {};
    public selectedOptions: Dictionary<IOption | undefined> = {};

    constructor(private balancePipeService: BalancePipe,
                private cdr: ChangeDetectorRef) {
    }

    ngOnChanges(_changes: SimpleChanges) {
        if (this.config) {
            for (let key in this.config) {
                switch (key) {
                    case "rounds":
                        this.roundCountOptions = this.config[key].map(v => {
                            return {
                                value: v,
                                text: v.toString()
                            };
                        });
                        if (!this.selectedOptions["roundCount"]) {
                            this.selectedOptions["roundCount"] = this.roundCountOptions[0];
                        }
                        break;
                    case "cashIncreaseStopStepper":
                        this.stepperConfigs[key] = {
                            ...this.config[key],
                            initialValue: this.currentBetAmount * 10
                        };
                        break;
                    case "cashDecreaseStopStepper":
                        this.stepperConfigs[key] = {
                            ...this.config[key],
                            initialValue: this.currentBetAmount * 10
                        };
                        break;
                    case "singleWinStopStepper":
                        this.stepperConfigs[key] = {
                            ...this.config[key],
                            minValue: this.currentBetAmount,
                            initialValue: this.currentBetAmount * 10
                        };
                        break;
                    case "autoCashOut":
                        this.stepperConfigs["autoCashOut"] = this.config["autoCashOut"];
                        break;
                    default:
                        this.stepperConfigs[key] = this.config[key];
                }
            }
        }
    }

    selectRoundCount(count: IOption<number> | undefined): void {
        if (count) {
            this.selectedOptions["roundCount"] = this.roundCountOptions.find(o => o.value === count.value);
            this.cdr.detectChanges();
        } else {
            delete this.selectedOptions["roundCount"];
        }
    }

    selectAmount(field: KeyOf<Pick<IAutoplayOptions & {
                     autoCashOut: number
                 }, "maxAccumulatedAmount" | "maxStackIncreaseAmount" | "maxStackDecreaseAmount" | "autoCashOut">>,
                 amount: number | undefined): void {
        if (amount) {
            this.selectedOptions[field] = {
                value: amount,
                text: ""
            };
        } else {
            delete this.selectedOptions[field];
        }
        this.cdr.detectChanges();
    }

    handleStepperVisibilityChange(field: KeyOf<Pick<IAutoplayOptions & {
                                      autoCashOut: number
                                  }, "maxAccumulatedAmount" | "maxStackIncreaseAmount" | "maxStackDecreaseAmount" | "autoCashOut">>,
                                  visible: boolean): void {
        if (!visible) {
            this.selectAmount(field, undefined);
        } else if (!this.selectedOptions[field]) {
            switch (field) {
                case "maxAccumulatedAmount":
                    this.selectAmount(field, this.stepperConfigs["singleWinStopStepper"].initialValue);
                    break;
                case "maxStackDecreaseAmount":
                    this.selectAmount(field, this.stepperConfigs["cashDecreaseStopStepper"].initialValue);
                    break;
                case "maxStackIncreaseAmount":
                    this.selectAmount(field, this.stepperConfigs["cashIncreaseStopStepper"].initialValue);
                    break;
                case "autoCashOut":
                    this.selectAmount(field, this.stepperConfigs["autoCashOut"].initialValue);
                    break;
            }
        }
    }

    start(): void {
        const selectedValues: Partial<IAutoplayOptions> = {};
        for (let key in this.selectedOptions) {
            selectedValues[key] = this.selectedOptions[key]?.value;
        }
        if (!selectedValues["maxStackDecreaseAmount"]) {
            selectedValues["maxStackDecreaseAmount"] = this.stepperConfigs["cashDecreaseStopStepper"].initialValue;
        }
        this.startAutoplay.emit(selectedValues);
    }

    formatBalanceStepperNumber = (value: number) => {
        return this.balancePipeService.transform(value, true, true) ?? value.toString();
    };

    formatStepperNumber = (value: number) => {
        return `x${value.toFixed(2)}`;
    };

    reset(): void {
        this.selectedOptions = {};
        this.cdr.detectChanges();
    }
}
